import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { Copy16 } from '@makyoapp/makyo-icon/icon'
import { ModalGrid } from '@makyoapp/makyo-ui'

import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useSingleProjectContext } from '@/context/SingleProjectContext'
import { useIsProjectDb } from '@/hooks/useIsProjectDb'
import { useParamId } from '@/hooks/useParamId'
import { ORG_DATABASE_ROUTE, ORG_ROUTE, PROJECT_ROUTE } from '@/shared/constants'
import { EVENT_KEYS } from '@/shared/custom-event'
import { useGridV2Context } from '@/components/GridV2/contexts/GridV2Context'

/**
 * @note only supports query table for now
 */
export const DuplicateTable = () => {
  const { t } = useTranslation()
  const orgId = useParamId('org')
  const navigate = useNavigate()
  const isProject = useIsProjectDb()
  const { project } = useSingleProjectContext()

  const { dbId, dbClient } = useDbClientContext()
  const { table } = useGridV2Context()

  const [show, setShow] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleDuplicate = async () => {
    if (!dbClient || !table) return

    setLoading(true)

    try {
      const newTable = await dbClient.DuplicateTable(table.isSnapshot ? 'snapshot' : 'query', table.id)
      const newTableId = newTable.id

      // wait for the page generation to finish (in app builder)
      if (isProject) {
        await new Promise<void>((resolve) => {
          const onPageProcessFinished = () => {
            window.removeEventListener(EVENT_KEYS.PAGE_PROCESS_FINISHED, onPageProcessFinished)
            resolve()
          }
          window.addEventListener(EVENT_KEYS.PAGE_PROCESS_FINISHED, onPageProcessFinished)

          window.dispatchEvent(
            new CustomEvent(EVENT_KEYS.DB_TABLE_DUPLICATED, {
              detail: {
                fromTableId: table.id,
                newTableId,
              },
            })
          )
        })
      }

      // redirect to new table
      if (orgId) {
        navigate(`${ORG_ROUTE}/${orgId}/${ORG_DATABASE_ROUTE}/${dbId}/${newTableId}`)
      } else if (isProject) {
        navigate(`${PROJECT_ROUTE}/${project?.id}/${ORG_DATABASE_ROUTE}/${newTableId}`)
      }

      window.dispatchEvent(new CustomEvent('db-client-reload'))
      toast.success(t('Query table duplicated'))
      //
    } catch (error) {
      console.error(error)
      toast.error(t('Failed to duplicate query table'))
    } finally {
      setLoading(false)
    }
  }

  if (!table) return null

  return (
    <>
      <button onClick={() => setShow(true)} className='mk-ddi flex-c mk-dd-row'>
        <Copy16 />
        <div>
          <Trans>Duplicate {table.isSnapshot ? 'Snapshot' : table.isQuery ? 'Query' : 'Table'}</Trans>
        </div>
      </button>

      <ModalGrid
        show={show}
        id={`${table.id}-modal`}
        title={table.isSnapshot ? t('Duplicate Snapshot') : table.isQuery ? t('Duplicate Query') : t('Duplicate Table')}
        isLoading={loading}
        onConfirm={handleDuplicate}
        onCancel={() => setShow(false)}
        onHide={() => setShow(false)}
        primaryTrailing={t('Yes, Duplicate')}
      >
        <div>
          <div className='text-sm mb-2'>{t('Are you sure you want to duplicate this query?')}</div>
          <div className='text-light'>
            {t('This will generate a copy of current query table that inherits all the columns and settings.')}
          </div>
        </div>
      </ModalGrid>
    </>
  )
}
